<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
    v-test="'select-franchise'"
  >
    <BaseDropdown
      v-model="companyId"
      :options="dropdownOptions"
      :small="$screen !== 's'"
      searchAutoFocus
      :searchable="franchiseCompanies.length > 7"
      :inverted="$screen === 's'"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import eventBus from '@/event-bus';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import type { Company } from '@/types';

const { user } = useUserStore();

const franchiseHistory = useStorage('franchise-history', [] as number[]);

const { onResult } = useQuery(gql`
  query getFranchiseCompanies {
    franchiseCompanies {
      id
      internalName
      name
      primaryLocationId
    }
  }
`);

const franchiseCompanies = ref<
  {
    id: Company['id'];
    internalName: Company['internalName'];
    name: Company['name'];
    primaryLocationId: Company['primaryLocationId'];
  }[]
>([]);

onResult(({ data }) => {
  franchiseCompanies.value = data.franchiseCompanies;
});

const router = useRouter();

const isSalonizedFranchiseAdmin = () =>
  user?.salonizedAdmin && !user?.impersonated;

const dropdownOptions = computed(() => {
  const baseOptions = franchiseCompanies.value.map((company) => ({
    value: company.id,
    label: company.internalName,
  }));

  if (isSalonizedFranchiseAdmin()) {
    if (franchiseCompanies.value.length) {
      const historyOptions = franchiseHistory.value.map((id) => ({
        value: id,
        label:
          franchiseCompanies.value.find((company) => company.id === id)
            ?.internalName || '',
        icon: id === companyId.value ? null : 'clock',
      }));

      return [...historyOptions, ...baseOptions];
    }
  }

  return baseOptions;
});

const saveFirstUser = () => {
  const { companyId } = useCompanyStore();
  if (!franchiseHistory.value.length) {
    franchiseHistory.value.push(companyId as number);
  }
};

const removeDuplicatesFromHistory = (id: number) =>
  franchiseHistory.value.filter((historyId) => historyId !== id);

const trimHistory = (history: number[]) =>
  history.length > 4 ? history.slice(0, 4) : history; // keep only 3 selectable franchises

const addToHistory = (id: number) => {
  saveFirstUser();
  const filteredHistory = removeDuplicatesFromHistory(id);
  const newHistory = [id, ...filteredHistory];
  franchiseHistory.value = trimHistory(newHistory);
};

const companyId = computed({
  get() {
    const { companyId } = useCompanyStore();
    return companyId;
  },
  set(id: number) {
    if (isSalonizedFranchiseAdmin()) {
      addToHistory(id);
    }

    const { mutate, onDone } = useMutation(
      gql`
        mutation becomeFranchise($input: BecomeFranchiseInput!) {
          becomeFranchise(input: $input) {
            status
          }
        }
      `,
      {
        variables: {
          input: {
            id,
          },
        },
      },
    );

    mutate();

    onDone(() => {
      const win: Window = window;
      let url = window.location.origin;

      const newCompany = franchiseCompanies.value.find(
        (company) => company.id === id,
      );

      if (newCompany) {
        const route = router.resolve({
          name: router.currentRoute.name,
          params: {
            companyId: id,
            locationId: newCompany.primaryLocationId,
          },
        });

        if (route?.meta.moduleName === 'customers') {
          // customer overview routes don't exist on the new company so change back to the customers route
          const moduleRoute = router.resolve({
            name: route.meta.moduleName || '',
            params: {
              companyId: id,
              locationId: newCompany.primaryLocationId,
            },
          });

          url += moduleRoute.href;
        } else if (route) {
          url += route.href;
        }
      }

      win.location = url;
    });
  },
});

eventBus.$on('update-franchise-name', (internalName: string) => {
  const activeFranchise = franchiseCompanies.value.find(
    (company) => company.id === companyId.value,
  );
  if (activeFranchise) {
    activeFranchise.internalName = internalName;
  }
});
</script>

<style
  lang="scss"
  module
>
.base {
  &:not(.smallScreen) {
    width: 150px;
  }
}
</style>
